import { useTranslation } from 'react-i18next'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useEffect, useMemo } from 'react'

import { FormSection } from '../formSection/FormSection'
import { AddNewProgramFormModel } from '../AddNewProgram.types'
import { FieldSection } from '../formSection/fieldSection/FieldSection'

import { Alert, Button, Checkbox, Flex, FormField, Select, TextInput } from '@percent/lemonade'
import style from './PartnerFields.module.scss'
import { PartnerFieldOptions } from './partnerFieldOptions/PartnerFieldOptions'
import { FieldType } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'

export function PartnerFields() {
  const { t } = useTranslation()

  const {
    control,
    register,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext<AddNewProgramFormModel>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'partnerFields'
  })

  const partnerFields = useWatch({ control, name: 'partnerFields' })

  const fieldTypes = useMemo<{ label: string; value: FieldType }[]>(() => {
    return [
      { label: t('form.shortText'), value: 'short_text' },
      { label: t('form.multipleChoice'), value: 'multiple_choice' },
      { label: t('form.number'), value: 'number' },
      { label: t('form.select'), value: 'select' },
      { label: t('form.website'), value: 'website' }
    ]
  }, [t])

  const onPartnerFieldChange = (index: number, type: FieldType) => {
    setValue(`partnerFields.${index}.type`, type)

    switch (type) {
      case 'multiple_choice':
        setValue(`partnerFields.${index}.options`, [{ key: '', value: '' }])
        setValue(`partnerFields.${index}.placeholder`, undefined)
        break
      case 'select':
        setValue(`partnerFields.${index}.options`, [{ key: '', value: '' }])
        break
      default:
        setValue(`partnerFields.${index}.options`, undefined)
        break
    }
  }

  const arePartnerFieldsRequired = (index: number) =>
    !!partnerFields?.[index]?.title || !!partnerFields?.[index]?.id || !!partnerFields?.[index]?.type

  useEffect(() => {
    trigger()
  }, [partnerFields, trigger])

  return (
    <FormSection title={t('form.partnerFields')}>
      {!!errors.partnerFields && <Alert variant="warning" title={t('form.enterAllRequiredFields')} />}
      {fields.map((field, index) => (
        <FieldSection key={field.id}>
          <Flex direction="row" gap={16}>
            <FormField
              label={t('form.addTitle')}
              statusMessage={errors.primaryColor?.message}
              necessity={arePartnerFieldsRequired(index) ? 'required' : undefined}
            >
              <TextInput {...register(`partnerFields.${index}.title`)} placeholder={t('form.addTitlePlaceholder')} />
            </FormField>
            <FormField
              label={t('form.id')}
              statusMessage={errors.primaryColor?.message}
              necessity={arePartnerFieldsRequired(index) ? 'required' : undefined}
            >
              <TextInput {...register(`partnerFields.${index}.id`)} placeholder={t('form.idPlaceholder')} />
            </FormField>
          </Flex>
          <FormField
            label={t('form.type')}
            necessity={arePartnerFieldsRequired(index) ? 'required' : undefined}
            statusMessage={errors.primaryColor?.message}
          >
            <Select
              options={fieldTypes}
              defaultValue={fieldTypes.find(type => type.value === field.type)}
              searchable
              placeholder={t('form.typePlaceholder')}
              onChange={({ value }) => {
                onPartnerFieldChange(index, value as FieldType)
              }}
            />
          </FormField>
          {partnerFields?.[index]?.type !== 'multiple_choice' && (
            <FormField label={t('form.placeholder')} statusMessage={errors.primaryColor?.message}>
              <TextInput
                {...register(`partnerFields.${index}.placeholder`)}
                placeholder={t('form.placeholderPlaceholder')}
              />
            </FormField>
          )}
          <Checkbox
            variant="default"
            active={partnerFields?.[index]?.validation?.required}
            name="partnerFieldsRequired"
            label={t('form.required')}
            onChange={event => {
              const isChecked = (event as React.ChangeEvent<HTMLInputElement>).target.checked
              setValue(`partnerFields.${index}.validation.required`, isChecked)
            }}
            disabled={false}
          />
          {(partnerFields?.[index]?.type === 'multiple_choice' || partnerFields?.[index]?.type === 'select') && (
            <PartnerFieldOptions partnerFieldIndex={index} />
          )}
          <div>
            <Button variant="secondary" size="small" onClick={() => remove(index)}>
              {t('form.removePartnerField')}
            </Button>
          </div>
        </FieldSection>
      ))}
      <div className={style.addNewPartnerFieldBtnWrapper}>
        <Button
          variant="secondary"
          size="small"
          onClick={() =>
            append({
              id: '',
              title: '',
              type: undefined as unknown as FieldType,
              validation: { required: false }
            })
          }
        >
          {t('form.addNewPartnerField')}
        </Button>
      </div>
    </FormSection>
  )
}
